import {
  getServiceAliases,
  getServiceMapping,
  getServiceComponents,
  getDependentServices,
  getReleaseBasedOnJobs,
  getReleaseBasedServiceComponents,
  getReleaseBasedDependentServices,
} from "../Services";
import { createColorCodeForRelease, vendorDict } from "../utility";
/** Build a flat list of all services: aliases keys + values */
const buildAllServices = (aliases) => {
  let all = new Set(Object.keys(aliases || {}));
  Object.values(aliases || {}).forEach(arr => (arr || []).forEach(s => all.add(s)));
  return Array.from(all);
};

/** Compute unique, sorted lists for filters */
const getList = (data, key) => {
  const s = new Set();
  data.forEach(item => {
    const v = item[key];
    if (v) s.add(v);
  });
  return Array.from(s).sort();
};

/** Filter releases by user preference selections */
const filterSelectedInPreferences = (preference, releases) => {
  return (releases || []).filter(r => {
    const vendKey = vendorDict[r["Vendor_type"]];
    return vendKey in preference &&
           preference[vendKey] &&
           preference[vendKey].includes(r["vendor_service"]);
  });
};

/** 1) Fetch aliases/mapping/components/dependents for the chosen services */
export async function fetchAliasAndMappings(services) {
  const body = { services };
  const [a, m, c, d] = await Promise.allSettled([
    getServiceAliases(body),
    getServiceMapping(body),
    getServiceComponents(body),
    getDependentServices(body),
  ]);

  const aliases   = a?.value?.data || {};
  const mapping   = Object.fromEntries(Object.entries(m?.value?.data || {}).sort());
  const comps     = Object.fromEntries(Object.entries(c?.value?.data || {}).sort());
  const depends   = Object.fromEntries(Object.entries(d?.value?.data || {}).sort());

  return { aliases, mapping, components: comps, dependents: depends };
}

/** 2) Fetch vendor (source) releases, merging by release_id into a Map */
export async function fetchVendorReleases(preference, sources, onProgress, onPartial) {
  const map = new Map();
  for (const releaseType of sources) {
    if (onProgress) await onProgress(releaseType.toLowerCase()); // e.g. "bfr", "enterprise", etc.
    if (vendorDict[releaseType] && preference[vendorDict[releaseType]]) {
      const body = {
        showLongRelease: preference["show_long_release"],
        longReleaseDuration: preference["long_release"],
        releaseType,
        services: preference[vendorDict[releaseType]],
      };
      const resp = await getReleaseBasedOnJobs(body);
      (resp?.data || []).forEach(item => map.set(item.release_id, item));
      onPartial?.({ phase: "vendor", data: Array.from(map.values()) });
    }
  }
  return map;
}

/** 3) Fetch Service Component releases */
export async function fetchComponentReleases(preference, allServices, onProgress) {
  if (onProgress) await onProgress("Service Components");
  const body = {
    showLongRelease: preference["show_long_release"],
    longReleaseDuration: preference["long_release"],
    services: allServices,
  };
  const resp = await getReleaseBasedServiceComponents(body);
  return resp?.data || [];
}

/** 4) Fetch Dependent Service releases */
export async function fetchDependentReleases(preference, allServices, onProgress) {
  if (onProgress) await onProgress("Dependent Service");
  const body = {
    showLongRelease: preference["show_long_release"],
    longReleaseDuration: preference["long_release"],
    services: allServices,
  };
  const resp = await getReleaseBasedDependentServices(body);
  return resp?.data || [];
}

/**
 * High-level orchestrator:
 * - fetch vendor releases
 * - fetch component + dependent releases
 * - apply preference filter for component/dependent sets
 * - merge into a single array
 * - compute color coding + filter lists
 */
export async function loadReleaseData({ preference, sources, aliases, onPartial }, onProgress) {
  // 1) vendor releases (base) 
  let releaseMap = await fetchVendorReleases(preference, sources, onProgress, onPartial);

  // 2) components/dependents (based on alias-expanded service universe)
  const allServices = buildAllServices(aliases);

 const compAll = await fetchComponentReleases(preference, allServices, onProgress);
 const compFiltered = filterSelectedInPreferences(preference, compAll);
 compFiltered.forEach(item => releaseMap.set(item.release_id, item));
 onPartial?.({ phase: "components", data: Array.from(releaseMap.values()) });

 const depAll = await fetchDependentReleases(preference, allServices, onProgress);
 const depFiltered = filterSelectedInPreferences(preference, depAll);
 depFiltered.forEach(item => releaseMap.set(item.release_id, item));
 onPartial?.({ phase: "dependents", data: Array.from(releaseMap.values()) });


  const hasServiceComponentReleases = compAll.length !== compFiltered.length;
  const hasDependentServiceReleases = depAll.length !== depFiltered.length;

  [...compFiltered, ...depFiltered].forEach(item => releaseMap.set(item.release_id, item));

  const data = Array.from(releaseMap.values());

  if (onProgress) await onProgress("Preparing data…");
  const statusList  = getList(data, "release_status");
  const envList     = getList(data, "environment").filter(e => e.toLowerCase() !== "all");
  const tierList    = ["N/A", ...getList(data, "tier")];

  return {
    data,
    statusList,
    envList,
    tierList,
    hasServiceComponentReleases,
    hasDependentServiceReleases,
  };
}
